import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { usePinnedPresetRoutes, type PinnedPresetRoute } from '@/entities/settings';

/** Раздел списка диалогов, открытый адресом */
export type DialogsSection = {
  /** Ключ рабочей записи раздела в `tables` — вид списка, который раздел за собой ведёт */
  sectionKey: string;
  /** Маршрут раздела закреплённого пресета; `undefined` — открыт раздел «Диалоги» */
  sectionRoute?: PinnedPresetRoute;
  /**
   * Адрес указывает на раздел закреплённого пресета, которого нет: пресет откреплён, удалён
   * или переименован (слаг считается от названия), либо это ссылка в никуда.
   */
  isUnknownSection: boolean;
  /** Разделы всех закреплённых пресетов таблицы — по пункту левого меню на каждый */
  pinnedRoutes: PinnedPresetRoute[];
};

/**
 * Раздел списка диалогов, в котором открыт список: раздел «Диалоги» либо раздел закреплённого
 * пресета.
 *
 * Единственное место, где адрес разбирается в раздел. Разбор один, а нужен потребителям разными
 * сторонами — ключ вида (список ведёт вид своего раздела), сам пресет (им раздел открывается
 * впервые) и перечень разделов (выбор пресета переводит между ними), — поэтому хук отдаёт их
 * вместе: по отдельности они разошлись бы в трактовке одного и того же адреса.
 *
 * Раздел «Диалоги» и раздел каждого закреплённого пресета ведут вид списка независимо: у каждого
 * свои фильтры, сортировка, колонки и свой выбранный пресет, поэтому переход между разделами
 * показывает то, что пользователь в разделе оставил. Адрес, которому не отвечает ни один
 * закреплённый пресет, уводит в «Диалоги» (`usePresetSectionSync`), поэтому и вид до перехода
 * берётся из этого раздела.
 *
 * @param tableName - Имя таблицы (например, `'dialogsListV2'`): список закреплённых пресетов
 * один на все разделы таблицы.
 */
export const useDialogsSection = (tableName: string): DialogsSection => {
  const { slug } = useParams<{ slug?: string }>();
  const pinnedRoutes = usePinnedPresetRoutes(tableName);

  return useMemo(() => {
    const sectionRoute = slug ? pinnedRoutes.find((route) => route.slug === slug) : undefined;
    return {
      sectionKey: sectionRoute?.sectionKey ?? tableName,
      sectionRoute,
      isUnknownSection: Boolean(slug) && !sectionRoute,
      pinnedRoutes,
    };
  }, [slug, pinnedRoutes, tableName]);
};
